<template>
  <b-container fluid class="home-padding">
    <Sector :is-first="true" class="mb-4">
      <ChooseExperience />
    </Sector>

    <Sector class="my-0">
      <Title />
    </Sector>

    <Sector class="mb-5">
      <GridTabsExperience />
    </Sector>

    <Sector class="mb-5">
      <WelcomeToVexels />
    </Sector>

    <Sector v-if="listExist('prehome_tshirt_designs')" class="mb-5">
      <Subtitle>
        <template #text>{{ $t('home.pre-home.tshirtCarousel.title') }}</template>
        <template #side>
          <Button
            shape="outline"
            size="small"
            font-size="13"
            color="white"
            text="gray"
            hover="exp"
            :to="$trailingSlash(localePath('merch-latest-designs'))"
          >
            {{ $t('home.pre-home.tshirtCarousel.button') }}
          </Button>
        </template>
      </Subtitle>
      <Carousel
        :items="list('prehome_tshirt_designs')"
        :height="carouselHeight"
        :see-more-enabled="true"
        :see-more-link="$trailingSlash(localePath('merch-latest-designs'))"
        @vector-click="vectorClick"
      >
        <template #overlay="slotProps">
          <NormalVectorOverlay :data="slotProps.data" />
        </template>
      </Carousel>
    </Sector>

    <Sector v-if="listExist('prehome_illustrations')" class="mb-5">
      <Subtitle>
        <template #text>{{ $t('home.pre-home.illustrationsCarousel.title') }}</template>
        <template #side>
          <Button
            shape="outline"
            size="small"
            font-size="13"
            color="white"
            text="gray"
            hover="design"
            :to="$trailingSlash(localePath('design-latest-vectors'))"
          >
            {{ $t('home.pre-home.illustrationsCarousel.button') }}
          </Button>
        </template>
      </Subtitle>
      <Carousel
        :items="list('prehome_illustrations')"
        :height="carouselHeight"
        :see-more-enabled="true"
        :see-more-link="$trailingSlash(localePath('design-latest-vectors'))"
        @vector-click="vectorClick"
      >
        <template #overlay="slotProps">
          <NormalVectorOverlay :data="slotProps.data" />
        </template>
      </Carousel>
    </Sector>

    <Sector v-if="listExist('prehome_pngs')" class="mb-5">
      <Subtitle>
        <template #text>{{ $t('home.pre-home.pngsCarousel.title') }}</template>
        <template #side>
          <EditableColorsIcons
            :see-more-text="$t('home.pre-home.pngsCarousel.seeAll')"
            :show-see-more="false"
          />
          <ButtonsExperience v-if="!isMobile" merch-path="merch-png" design-path="svg-png" />
        </template>
      </Subtitle>
      <ButtonsExperience v-if="isMobile" merch-path="merch-png" design-path="svg-png" />
      <Carousel
        :items="list('prehome_pngs')"
        :height="170"
        :margin-right="15"
        :margin-bottom="45"
        :see-more-enabled="true"
        :border-radius="10"
        :see-more-link="$trailingSlash(localePath('svg-png'))"
        @vector-click="vectorClick"
      >
        <template #overlay="slotProps">
          <NormalVectorOverlay :data="slotProps.data" />
        </template>
      </Carousel>
    </Sector>

    <Sector v-if="listExist('prehome_tshirt_psd')" class="mb-5">
      <ScalablePsds />

      <Carousel
        :items="list('prehome_tshirt_psd')"
        :height="carouselHeight"
        :see-more-enabled="true"
        :see-more-link="$urlBuilder('search', { path: 'merch-psd', keyword: 't-shirt-template' })"
        class="mt-2"
        @vector-click="vectorClick"
      >
        <template #overlay="slotProps">
          <NormalVectorOverlay :data="slotProps.data" />
        </template>
      </Carousel>

      <div class="w-100 d-flex justify-content-center align-items-center mt-2">
        <Button
          shape="full"
          :to="
            $urlBuilder('search', {
              path: 'merch-psd',
              keyword: 't-shirt-template'
            })
          "
        >
          {{ $t('home.pre-home.tshirtPsd.button') }}
        </Button>
      </div>
    </Sector>

    <Sector class="mb-5">
      <MockupSection
        :title="$t('home.merch.sector7.title')"
        color="#3f80f1"
        color-img="https://cdn.vexels.com/img/color-guy.jpg"
        :color-img-alt="$t('home.pre-home.mockupSection.colouredMockupAlt')"
        white-img="https://cdn.vexels.com/img/white-guy.jpg"
        :white-img-alt="$t('home.pre-home.mockupSection.whiteMockupAlt')"
        :reserved-width="927"
        :reserved-height="606"
      >
        <template #action-btn>
          <div class="button-mockup col-12 col-sm-6 justify-content-end">
            <Button class="mr-3" shape="full" :to="$urlBuilder('landing', { uid: 'psd-mockups' })">
              {{ $t('home.pre-home.mockupSection.button') }}
            </Button>
          </div>
          <div class="button-mockup col-12 col-sm-6 justify-content-start">
            <Button class="ml-3" shape="full" to="https://mockups.vexels.com/" :blank="true">
              {{ $t('home.pre-home.mockupSection.button2') }}
            </Button>
          </div>
        </template>
      </MockupSection>
    </Sector>

    <Sector class="mb-5">
      <Makers />
    </Sector>

    <Sector v-if="listExist('prehome_mockups')" class="mb-5">
      <Subtitle>
        <template #text>{{ $t('home.pre-home.mockupCarousel.title') }}</template>
        <template #side>
          <ButtonsExperience v-if="!isMobile" merch-path="merch-psd" design-path="psd" />
        </template>
      </Subtitle>
      <ButtonsExperience v-if="isMobile" merch-path="merch-psd" design-path="psd" />
      <Carousel
        :items="list('prehome_mockups')"
        :height="carouselHeight"
        :see-more-enabled="true"
        :see-more-link="$urlBuilder('search', { path: 'merch-psd', keyword: 'mockup' })"
        @vector-click="vectorClick"
      >
        <template #overlay="slotProps">
          <NormalVectorOverlay :data="slotProps.data" />
        </template>
      </Carousel>
    </Sector>

    <Sector class="mb-5">
      <Makers :is-mobile="isMobile" />
    </Sector>

    <Sector v-if="listExist('prehome_collections')" class="mb-5">
      <Subtitle :margin-right="5">
        <template #text>{{ $t('home.design.sector.title6') }}</template>
        <template #side>
          <BadgeNew class="mr-1" />
        </template>
      </Subtitle>
      <Carousel
        :items="list('prehome_collections')"
        :height="carouselCollectionHeight"
        :margin-bottom="45"
        :see-more-enabled="true"
        :see-more-link="$trailingSlash(localePath('collections'))"
        :show-bottom-bar="true"
        :is-grouper="true"
        :is-collection="true"
      >
        <template #bottomBar="slotProps">
          <CollectionsBottomBar :data="slotProps.data" />
        </template>
      </Carousel>
    </Sector>

    <Sector class="mb-5">
      <CantFind
        :title="$t('home.pre-home.cantFind.title')"
        :description="$t('home.pre-home.cantFind.description')"
        :alt-image="$t('home.pre-home.cantFind.altImage')"
        :request-text="
          $auth.loggedIn
            ? $t('home.pre-home.cantFind.request')
            : $t('home.pre-home.cantFind.learnMore')
        "
      />
    </Sector>

    <Sector class="mb-5">
      <TrustedBy />
    </Sector>
  </b-container>
</template>

<script>
import { mapGetters } from 'vuex';

import BadgeNew from 'UI/Badges/New';
import Button from 'UI/Button';
import ButtonsExperience from '@/components/UI/ButtonsExperience.vue';
import CantFind from '@/components/Home/CantFind';
import Carousel from '@/components/Common/Carousel';
import CollectionsBottomBar from '@/components/Collections/CollectionsBottomBar';
import EditableColorsIcons from '@/components/Home/Design/EditableColorsIcons';
import GridTabsExperience from '@/components/Home/GridTabsExperience';
import Makers from '@/components/Home/Makers.vue';
import MockupSection from '@/components/Home/MockupSection';
import NormalVectorOverlay from '@/components/Vectors/NormalVectorOverlay';
import ScalablePsds from '@/components/Home/ScalablePsds';
import Sector from '@/components/Home/Sector';
import Subtitle from '@/components/Home/Subtitle';
import TrustedBy from '@/components/Common/TrustedBy';
import WelcomeToVexels from '@/components/Home/WelcomeToVexels';
import Title from '@/components/Home/Title';
import ChooseExperience from '@/components/Home/ChooseExperience';

export default {
  components: {
    BadgeNew,
    Button,
    ButtonsExperience,
    CantFind,
    Carousel,
    CollectionsBottomBar,
    ChooseExperience,
    EditableColorsIcons,
    GridTabsExperience,
    Makers,
    MockupSection,
    NormalVectorOverlay,
    ScalablePsds,
    Sector,
    Subtitle,
    TrustedBy,
    WelcomeToVexels,
    Title
  },
  data() {
    return {
      carouselHeight: 180,
      carouselCollectionHeight: 230,
      windowWidth: 992
    };
  },
  async fetch() {
    const prehome_tshirt_designs = this.$store.dispatch('listings/GET_LIST', {
      name: 'prehome_tshirt_designs',
      type: 'preview'
    });
    const prehome_illustrations = this.$store.dispatch('listings/GET_LIST', {
      name: 'prehome_illustrations',
      type: 'preview'
    });
    const prehome_pngs = this.$store.dispatch('listings/GET_LIST', {
      name: 'prehome_pngs',
      type: 'preview'
    });
    const prehome_tshirt_psd = this.$store.dispatch('listings/GET_LIST', {
      name: 'prehome_tshirt_psd',
      type: 'preview'
    });
    const prehome_mockups = this.$store.dispatch('listings/GET_LIST', {
      name: 'prehome_mockups',
      type: 'preview'
    });
    const prehome_collections = this.$store.dispatch('listings/GET_LIST', {
      name: 'prehome_collections',
      type: 'collection'
    });
    await Promise.all([
      prehome_tshirt_designs,
      prehome_illustrations,
      prehome_pngs,
      prehome_tshirt_psd,
      prehome_mockups,
      prehome_collections
    ]);
  },
  computed: {
    ...mapGetters('listings', ['list', 'listExist']),
    isMobile() {
      return this.windowWidth < 992;
    }
  },
  mounted() {
    window.addEventListener('resize', this.onResize);
    this.onResize();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },
  methods: {
    onResize() {
      this.windowWidth = window.innerWidth;
    },
    async vectorClick({ item, shownListing }, e) {
      e.preventDefault();
      this.$bvModal.show('preview-vector-modal');
      await this.$store.dispatch('vectors/OPEN_PREVIEW', { item, list: shownListing });
    }
  }
};
</script>

<style></style>
